import { useEffect, useRef, useCallback, useState } from 'react';

// Tiempo de advertencia antes de cerrar sesion (60 segundos)
const WARNING_TIME = 60 * 1000;

/**
 * Hook para detectar inactividad del usuario y ejecutar logout automático
 * @param {Function} onIdle - Función a ejecutar cuando se detecta inactividad
 * @param {number} idleTime - Tiempo de inactividad en milisegundos (default: 15 minutos)
 * @param {boolean} isActive - Si el hook debe estar activo (solo cuando hay sesión)
 */
const useIdleTimeout = (onIdle, idleTime = 15 * 60 * 1000, isActive = true) => {
    const timeoutRef = useRef(null);
    const warningTimeoutRef = useRef(null);
    const lastActivityRef = useRef(Date.now());
    const [showWarning, setShowWarning] = useState(false);
    const [remainingTime, setRemainingTime] = useState(WARNING_TIME / 1000);
    const countdownRef = useRef(null);

    // Funcion para iniciar cuenta regresiva
    const startCountdown = useCallback(() => {
        setRemainingTime(WARNING_TIME / 1000);
        countdownRef.current = setInterval(() => {
            setRemainingTime(prev => {
                if (prev <= 1) {
                    clearInterval(countdownRef.current);
                    return 0;
                }
                return prev - 1;
            });
        }, 1000);
    }, []);

    // Funcion para detener cuenta regresiva
    const stopCountdown = useCallback(() => {
        if (countdownRef.current) {
            clearInterval(countdownRef.current);
            countdownRef.current = null;
        }
        setShowWarning(false);
        setRemainingTime(WARNING_TIME / 1000);
    }, []);

    // Resetear el timer de inactividad
    const resetTimer = useCallback(() => {
        lastActivityRef.current = Date.now();

        // Guardar última actividad en localStorage para sincronizar entre pestañas
        localStorage.setItem('lastActivity', Date.now().toString());

        // Ocultar advertencia si estaba visible
        stopCountdown();

        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
        }
        if (warningTimeoutRef.current) {
            clearTimeout(warningTimeoutRef.current);
        }

        // Timer para mostrar advertencia (tiempo total - 60 segundos)
        const warningDelay = idleTime - WARNING_TIME;
        if (warningDelay > 0) {
            warningTimeoutRef.current = setTimeout(() => {
                setShowWarning(true);
                startCountdown();
            }, warningDelay);
        }

        // Timer para cerrar sesion
        timeoutRef.current = setTimeout(() => {
            stopCountdown();
            onIdle();
        }, idleTime);
    }, [onIdle, idleTime, stopCountdown, startCountdown]);

    // Verificar si la sesión expiró (para cuando el usuario vuelve a la pestaña)
    const checkIfExpired = useCallback(() => {
        const lastActivity = localStorage.getItem('lastActivity');
        if (lastActivity) {
            const timeSinceLastActivity = Date.now() - parseInt(lastActivity, 10);
            if (timeSinceLastActivity >= idleTime) {
                onIdle();
                return true;
            }
        }
        return false;
    }, [onIdle, idleTime]);

    useEffect(() => {
        // Si no está activo (no hay sesión), no hacer nada
        if (!isActive) {
            return;
        }

        // Verificar al montar si ya expiró
        if (checkIfExpired()) {
            return;
        }

        // Eventos que indican actividad del usuario
        const events = [
            'mousedown',
            'mousemove',
            'keydown',
            'scroll',
            'touchstart',
            'click',
            'wheel'
        ];

        // Handler para eventos de actividad
        const handleActivity = () => {
            resetTimer();
        };

        // Handler para cuando el usuario vuelve a la pestaña
        const handleVisibilityChange = () => {
            if (document.visibilityState === 'visible') {
                if (!checkIfExpired()) {
                    resetTimer();
                }
            }
        };

        // Handler para sincronizar entre pestañas
        const handleStorageChange = (e) => {
            if (e.key === 'logout') {
                // Otra pestaña cerró sesión
                onIdle();
            }
        };

        // Agregar listeners
        events.forEach(event => {
            document.addEventListener(event, handleActivity, { passive: true });
        });
        document.addEventListener('visibilitychange', handleVisibilityChange);
        window.addEventListener('storage', handleStorageChange);

        // Iniciar timer
        resetTimer();

        // Cleanup
        return () => {
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }
            if (warningTimeoutRef.current) {
                clearTimeout(warningTimeoutRef.current);
            }
            if (countdownRef.current) {
                clearInterval(countdownRef.current);
            }
            events.forEach(event => {
                document.removeEventListener(event, handleActivity);
            });
            document.removeEventListener('visibilitychange', handleVisibilityChange);
            window.removeEventListener('storage', handleStorageChange);
        };
    }, [resetTimer, checkIfExpired, onIdle, isActive]);

    return {
        resetTimer,
        getLastActivity: () => lastActivityRef.current,
        showWarning,
        remainingTime,
        dismissWarning: stopCountdown
    };
};

export default useIdleTimeout;
